/** What leaves for Telegram and what never does (STORY-122, FR-058, PRD Section 4).
 *
 * The one source of the wording: the student's live screen and the settings page mount it,
 * and the course setting links to it. Every row of the PRD's two tables is a key under
 * `tg.disclosure.leaves.*` and `tg.disclosure.never.*`, listed here as arrays so a test can
 * hold the component to the PRD: a new outbound field fails
 * `services/integrations/tests/test_telegram_consent.py` until it is added to the table, to
 * both string files and to the array below.
 *
 * Read on a phone before a lesson and at 200% interface size: rem-based text, one column,
 * no breakpoint split, nothing on colour alone.
 */
import { useTranslation } from "react-i18next";

/** Bump when the wording changes in substance; the service keeps it on the link.
 * 2026-09-12 (STORY-127): ending it also asks Telegram to delete the bot's own questions
 * still inside its 48-hour window, and states what remains outside the product's reach.
 * 2026-09-13 (STORY-128): after a reveal the chat may hear the student's own verdict and,
 * where the school's setup allows it, the class counts; class totals leave "never".
 * 2026-09-14 (STORY-129): for a short-answer or numeric question the reply the student types
 * leaves, only where the school's setup allows typed replies; a link made under an older
 * wording never gets a typed question, whatever the deployment's flag. */
export const DISCLOSURE_VERSION = "2026-09-14";

/** Section 4, "what leaves, per round, for each opted-in student", row by row. The last two
 * (STORY-128, FR-059) leave only after a reveal: the student's own verdict, as the session
 * service gave it, when the teacher reveals in `correct` mode; the class counts, never who,
 * when the teacher reveals in `distribution` mode and the deployment has
 * `SVC_TELEGRAM_SHARE_DISTRIBUTION` on (off by default). Both are disclosed whether or not
 * the deployment sends them, so the wording is true on every deployment. The last
 * (STORY-129, FR-061) is the reply a student types for a short-answer or numeric question,
 * which leaves only where the deployment has `SVC_TELEGRAM_TYPED_REPLIES` on (off by
 * default): free text through a third party is its own row, and the service's tests
 * refuse the flag without it. */
export const LEAVES = ["stem", "options", "lang", "multi", "tap", "timing", "chatId", "joinLink", "verdict", "distribution", "typed"] as const;
/** Section 4, "what never leaves, and is enforced in code", row by row. */
export const NEVER = ["name", "ids", "key", "others", "context", "images"] as const;

export function Disclosure() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4 text-[0.9375rem] leading-relaxed">
      <p>{t("tg.disclosure.where")}</p>
      <section aria-labelledby="tg-leaves">
        <h3 id="tg-leaves" className="mb-1 font-semibold">{t("tg.disclosure.leaves.title")}</h3>
        <ul className="list-disc pl-5">
          {LEAVES.map((k) => (
            <li key={k}>{t(`tg.disclosure.leaves.${k}`)}</li>
          ))}
        </ul>
      </section>
      <section aria-labelledby="tg-never">
        <h3 id="tg-never" className="mb-1 font-semibold">{t("tg.disclosure.never.title")}</h3>
        <ul className="list-disc pl-5">
          {NEVER.map((k) => (
            <li key={k}>{t(`tg.disclosure.never.${k}`)}</li>
          ))}
        </ul>
      </section>
      <section aria-labelledby="tg-third" className="flex flex-col gap-1">
        <h3 id="tg-third" className="font-semibold">{t("tg.disclosure.thirdParty.title")}</h3>
        <p>{t("tg.disclosure.thirdParty.servers")}</p>
        <p>{t("tg.disclosure.thirdParty.notEncrypted")}</p>
        <p>{t("tg.disclosure.thirdParty.noDelete")}</p>
        <p className="text-[color:var(--ink-2)]">{t("tg.disclosure.thirdParty.risk")}</p>
      </section>
      <section aria-labelledby="tg-ending" className="flex flex-col gap-1">
        <h3 id="tg-ending" className="font-semibold">{t("tg.disclosure.ending.title")}</h3>
        <p>{t("tg.disclosure.ending.how")}</p>
        <p>{t("tg.disclosure.ending.removes")}</p>
        <p>{t("tg.disclosure.ending.remains")}</p>
      </section>
    </div>
  );
}

/** The plain statement beside the Disconnect action (STORY-127, FR-057): what disconnecting
 * deletes, what it removes from the chat, and what is outside the product's reach. */
export function EndingBefore() {
  const { t } = useTranslation();
  return <p className="text-[0.9375rem] leading-relaxed text-[color:var(--ink-2)]">{t("tg.disclosure.ending.before")}</p>;
}

/** The same boundary, stated after the erasure, chosen by what happened. The counts come
 * from the erase route when this screen did the erasing: the plain statement when every
 * question inside the window was removed, the one with the numbers when some were not (a
 * refusal, a timeout), and one that claims no removal when there was nothing in the window
 * to remove, so the screen never claims a question was removed that is still there, or
 * that was never there. The counts line is dropped when both numbers are zero. A disconnect made elsewhere (the settings page's own button, /stop in the chat)
 * shows the statement that is true for both: what is in the chat is outside our reach. The
 * bot's final message in the chat carries the same wording. */
export function EndingAfter({ deleted, outOfReach, failed = 0 }: { deleted?: number; outOfReach?: number; failed?: number }) {
  const { t } = useTranslation();
  const counted = deleted !== undefined && outOfReach !== undefined;
  return (
    <div role="status" className="flex flex-col gap-1 text-[0.9375rem] leading-relaxed">
      {!counted && <p>{t("tg.disclosure.ending.afterElsewhere")}</p>}
      {counted && failed > 0 && <p>{t("tg.disclosure.ending.afterPartial", { deleted, failed })}</p>}
      {counted && failed === 0 && deleted === 0 && <p>{t("tg.disclosure.ending.afterNone")}</p>}
      {counted && failed === 0 && deleted > 0 && <p>{t("tg.disclosure.ending.after")}</p>}
      {counted && (deleted > 0 || outOfReach > 0) && <p className="text-[color:var(--ink-2)]">{t("tg.disclosure.ending.afterCounts", { deleted, outOfReach })}</p>}
    </div>
  );
}
